import { createReducer, on } from '@ngrx/store';
import { TicksheetActions } from './ticksheet.actions';
import { Pagination } from '@core/interface/core';
import {
  FormDataByID,
  FormListData,
  FormResponseData,
  SorOptionsFormData,
  Ticksheet,
  TicksheetData,
} from '@core/interface/ticksheet';

export const TicksheetFeatureKey = 'ticksheet';

export interface TicksheetState {
  forms: {
    list: FormListData[];
    isLoading: boolean;
    pagination: Pagination;
    isAddingData: boolean;
    formUpdated: boolean;
  };
  headers: {
    isFormHeaderLoading: boolean;
    data: FormResponseData | null;
  };
  selectedFormData: {
    data?: FormDataByID | null;
    isLoading: boolean;
  };
  ticksheet: {
    list: Ticksheet[];
    pagination: Pagination;
    isLoading: boolean;
    selectedTimesheet: TicksheetData | null;
    isUpdating: boolean;
    isUpdated: boolean;
    detailsUpdated: boolean;
    detailsUpdating: boolean;
  };
  sorOptions: {
    data: SorOptionsFormData | null;
    isLoading: boolean;
  };
}

export const initialState: TicksheetState = {
  forms: {
    list: [],
    isLoading: true,
    isAddingData: false,
    formUpdated: false,
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 100,
    },
  },
  headers: { data: null, isFormHeaderLoading: false },
  selectedFormData: {
    isLoading: false,
  },
  ticksheet: {
    list: [],
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 100,
    },
    isLoading: false,
    selectedTimesheet: null,
    isUpdating: false,
    isUpdated: false,
    detailsUpdating: false,
    detailsUpdated: false,
  },
  sorOptions: {
    data: null,
    isLoading: false,
  },
};

export const TicksheetReducer = createReducer(
  initialState,
  on(TicksheetActions.loadTicksheetForms, (state) => ({
    ...state,
    forms: {
      ...state.forms,
      isLoading: true,
    },
  })),
  on(
    TicksheetActions.loadTicksheetFormsSuccess,
    (state, { data, pagination }) => ({
      ...state,
      forms: {
        ...state.forms,
        list: data,
        pagination,
        isLoading: false,
      },
    }),
  ),
  on(TicksheetActions.loadTicksheetFormsFailure, (state, { error }) => {
    return { ...state, isLoading: false };
  }),

  on(TicksheetActions.createFormHeader, (state) => ({
    ...state,
    headers: {
      ...state.headers,
      isFormHeaderLoading: true,
    },
  })),
  on(TicksheetActions.createFormHeaderFailure, (state, error) => ({
    ...state,
    headers: {
      ...state.headers,
      data: null,
      isFormHeaderLoading: false,
    },
  })),
  on(TicksheetActions.createFormHeaderSuccess, (state, { data }) => ({
    ...state,
    headers: {
      ...state.headers,
      data: data,
      isFormHeaderLoading: false,
    },
  })),

  on(TicksheetActions.addEditTicksheet, (state) => ({
    ...state,
    forms: {
      ...state.forms,
      isAddingData: true,
      formUpdated: false,
    },
  })),
  on(TicksheetActions.addTicksheetFailure, (state, error) => ({
    ...state,
    forms: {
      ...state.forms,
      isAddingData: false,
      formUpdated: false,
    },
  })),
  on(TicksheetActions.addTicksheetSuccess, (state, { data }) => ({
    ...state,
    forms: {
      ...state.forms,
      isAddingData: false,
      formUpdated: true,
    },
  })),

  on(TicksheetActions.loadFormDetails, (state) => ({
    ...state,
    selectedFormData: {
      ...state.selectedFormData,
      isLoading: true,
      data: null,
    },
  })),
  on(TicksheetActions.loadFormDetailsFailure, (state, error) => ({
    ...state,
    selectedFormData: {
      ...state.selectedFormData,
      isLoading: false,
      data: null,
    },
  })),
  on(TicksheetActions.loadFormDetailsSuccess, (state, { data }) => ({
    ...state,
    selectedFormData: {
      ...state.selectedFormData,
      isLoading: false,
      data,
    },
  })),
  on(TicksheetActions.resetFormFlag, (state) => ({
    ...state,
    forms: {
      ...state.forms,
      formUpdated: false,
    },
  })),
  on(TicksheetActions.resetTicksheetFormData, (state) => ({
    ...state,
    selectedFormData: {
      ...state.selectedFormData,
      data: null,
      isLoading: false,
    },
    ticksheet: {
      ...state.ticksheet,
      selectedTimesheet: null,
    },
    headers: {
      ...state.headers,
      data: null,
      isFormHeaderLoading: false,
    },
  })),

  on(TicksheetActions.loadTicksheets, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      list: [],
      isLoading: true,
    },
  })),
  on(TicksheetActions.loadTicksheetsSuccess, (state, { data, pagination }) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      list: data,
      pagination,
      isLoading: false,
    },
  })),
  on(TicksheetActions.loadTicksheetsFailure, (state, { error }) => {
    return { ...state, ticksheet: { ...state.ticksheet, isLoading: false } };
  }),
  on(TicksheetActions.loadCheckedTicksheets, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      list: [],
      isLoading: true,
    },
  })),
  on(
    TicksheetActions.loadCheckedTicksheetsSuccess,
    (state, { data, pagination }) => ({
      ...state,
      ticksheet: {
        ...state.ticksheet,
        list: data,
        pagination,
        isLoading: false,
      },
    }),
  ),
  on(TicksheetActions.loadCheckedTicksheetsFailure, (state, { error }) => {
    return { ...state, ticksheet: { ...state.ticksheet, isLoading: false } };
  }),

  on(TicksheetActions.loadTicksheetDetails, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      isLoading: true,
      selectedTimesheet: null,
      isUpdated: false,
    },
  })),
  on(TicksheetActions.loadTicksheetDetailsFailure, (state, error) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      isLoading: false,
      selectedTimesheet: null,
      isUpdated: false,
    },
  })),
  on(TicksheetActions.loadTicksheetDetailsSuccess, (state, { data }) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      isLoading: false,
      selectedTimesheet: data,
      isUpdated: false,
    },
  })),

  on(TicksheetActions.updateTicksheetRow, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      isUpdating: true,
      isUpdated: false,
    },
  })),
  on(TicksheetActions.updateTicksheetRowFailure, (state, error) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      isUpdating: false,
      isUpdated: false,
    },
  })),
  on(TicksheetActions.updateTicksheetRowSuccess, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      isUpdating: false,
      isUpdated: true,
      selectedTimesheet: null,
    },
  })),

  on(TicksheetActions.addTicksheetRow, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      isUpdating: true,
      isUpdated: false,
    },
  })),
  on(TicksheetActions.addTicksheetRowFailure, (state, error) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      isUpdating: false,
      isUpdated: false,
    },
  })),
  on(TicksheetActions.addTicksheetRowSuccess, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      isUpdating: false,
      isUpdated: true,
      selectedTimesheet: null,
    },
  })),

  on(TicksheetActions.updateTicksheetDetails, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      detailsUpdating: true,
    },
  })),
  on(TicksheetActions.updateTicksheetDetailsSuccess, (state, response) => {
    if (state.ticksheet.selectedTimesheet) {
      return {
        ...state,
        ticksheet: {
          ...state.ticksheet,
          detailsUpdating: false,
          detailsUpdated: true,
          selectedTimesheet: {
            ...state.ticksheet.selectedTimesheet,
            file_link: response.file_link,
            job_number: response.data.job_number,
          },
        },
      };
    }
    return {
      ...state,
      ticksheet: {
        ...state.ticksheet,
        detailsUpdating: false,
        detailsUpdated: true,
      },
    };
  }),
  on(TicksheetActions.updateTicksheetDetailsFailure, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      detailsUpdating: false,
      detailsUpdated: false,
    },
  })),
  on(TicksheetActions.resetUpdateTicksheetDetailsFlag, (state) => ({
    ...state,
    ticksheet: {
      ...state.ticksheet,
      detailsUpdated: false,
    },
  })),

  on(TicksheetActions.loadSorOptions, (state) => ({
    ...state,
    sorOptions: {
      data: null,
      isLoading: true,
    },
  })),
  on(TicksheetActions.loadSorOptionsSuccess, (state, { form_data }) => ({
    ...state,
    sorOptions: {
      data: form_data,
      isLoading: false,
    },
  })),
  on(TicksheetActions.loadSorOptionsFailure, (state) => ({
    ...state,
    sorOptions: {
      data: null,
      isLoading: false,
    },
  })),
);
